import { Component, HostListener, OnInit } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

/**
 * Install-prompt + update-available banner — logic ported verbatim from the
 * PO project's pwa-banner.component.ts (same beforeinstallprompt capture,
 * iOS fallback instructions, 24h dismiss persistence, and SwUpdate polling).
 * Only the template/CSS were rewritten as plain HTML instead of mat-card,
 * since reev-admin has no Angular Material installed.
 */
@Component({
  selector: 'app-pwa-banner',
  templateUrl: './pwa-banner.component.html',
  styleUrls: ['./pwa-banner.component.css']
})
export class PwaBannerComponent implements OnInit {
  private readonly DISMISS_KEY = 'pwa_install_dismissed';
  private readonly DISMISS_HOURS = 24;

  deferredPrompt: any = null;
  showInstallBanner = false;
  showIosBanner = false;
  showUpdateBanner = false;
  updating = false;

  constructor(private swUpdate: SwUpdate) {}

  ngOnInit(): void {
    this.detectIosAndShowPrompt();

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(evt => {
        if (evt.type === 'VERSION_READY') {
          this.showUpdateBanner = true;
        }
      });
      this.checkForUpdates();
      setInterval(() => this.checkForUpdates(), 5 * 60 * 1000);
      window.addEventListener('focus', () => this.checkForUpdates());
    }
  }

  private checkForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().catch(() => { /* offline or no SW yet */ });
    }
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(e: any): void {
    e.preventDefault();
    if (this.isDismissedRecently()) { return; }
    this.deferredPrompt = e;
    this.showInstallBanner = true;
  }

  @HostListener('window:appinstalled')
  onAppInstalled(): void {
    this.showInstallBanner = false;
    this.showIosBanner = false;
    this.deferredPrompt = null;
  }

  private detectIosAndShowPrompt(): void {
    const isIos = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isIos && !isStandalone && !this.isDismissedRecently()) {
      this.showIosBanner = true;
    }
  }

  private isDismissedRecently(): boolean {
    const dismissedAt = Number(localStorage.getItem(this.DISMISS_KEY) || 0);
    if (!dismissedAt) { return false; }
    const hoursSince = (Date.now() - dismissedAt) / (1000 * 60 * 60);
    return hoursSince < this.DISMISS_HOURS;
  }

  installApp(): void {
    if (!this.deferredPrompt) { return; }
    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then(() => {
      this.deferredPrompt = null;
      this.showInstallBanner = false;
    });
  }

  dismissInstall(): void {
    this.showInstallBanner = false;
    this.showIosBanner = false;
    localStorage.setItem(this.DISMISS_KEY, String(Date.now()));
  }

  updateApp(): void {
    this.updating = true;
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
